import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import HeadSub from '../../../../components/comm/HeadSub';
import WeeklyDateSwiper from '../../../../components/home/WeeklyDateSwiper';
import RecommendBox from '../../../../components/home/RecommendBox';
import ExerciseRecordCard from '../../../../components/home/exercise-record/ExerciseRecordCard';
import ExerciseStat from '../../../../components/home/exercise-record/ExerciseStat';
import ConfirmModal from '../../../../components/comm/ConfirmModal';
import Modal from '../../../../components/comm/Modal';

import ExerciseDurationStat from '../../../../components/home/exercise-record/ExerciseDurationStat';

import axiosApi from '../../../../api/daonAxios';

import {
    getExerciseList,
    getExerciseSummary,
    deleteExercise,
    toRecDe,
    toIntensityKey,
    formatRecTm,
} from '../../../../api/exerciseAPI';

import { EXERCISE_ACCESS, useWatchExerciseSync } from '../../../../hook/useWatchExerciseSync';
import { openSamsungHealthStore } from '../../../../bridge/healthBridge';

import URL from '../../../../constants/url';
import style from './ExerciseRecordMain.module.css';

import iconExclMark from '../../../../images/common/i_excl_mark.svg';

/** 일일 운동 목표. 30분은 기획 확정값이고 전 사용자 동일하다.
 *  칼로리 목표는 아직 미확정이라 임시값이다. (운동-기록-기획확인-필요사항.md §1) */
const GOAL = { minutes: 30, calorie: 1000 };

const SOURCE_LABELS = {
    manual: '직접 기록',
    watch: '워치 연동 기록',
    health: '헬스커넥트 연동',
};

/** 날짜 바에 며칠치 점을 미리 받아둘지. 슬라이드 몇 장 분량이면 충분하다. */
const MARK_DAYS = 41;

/** 서버 기록 1건을 화면에서 쓰는 모양으로 바꾼다. */
const toViewItem = (hist) => ({
    id: hist.usrExrcHistId,
    name: hist.exrcNm,
    kcal: hist.cnsumCalor ?? 0,
    minutes: hist.exrcMin ?? 0,
    time: formatRecTm(hist.recTm),
    source: hist.cntcRecAt === '1' ? 'watch' : 'manual',
    intensity: toIntensityKey(hist.exrcInCd),
    exrcMngId: hist.exrcMngId,
    recDe: hist.recDe,
});

/** 목표 대비 달성률(%). */
const percent = (value, goal) => (goal > 0 ? Math.round((value / goal) * 100) : 0);

const ExerciseRecordMain = () => {
    const navigate = useNavigate();

    const [selectedDate, setSelectedDate] = useState(dayjs().startOf('day'));
    const [daily, setDaily] = useState(null);     // 해당 일자 기록 + 합계
    const [dayMarks, setDayMarks] = useState({}); // 날짜 바 점
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [permOpen, setPermOpen] = useState(false);

    const recDe = toRecDe(selectedDate.format('YYYY-MM-DD'));


    //ai 코멘트용
    const exerciseAiRequestRef = useRef(false);
    const exerciseAiPollingIdRef = useRef(null);
    const exerciseAiPollingTimerRef = useRef(null);
    
    const [exerciseAiComment, setExerciseAiComment] = useState('');
    const [exerciseAiCommentLoading, setExerciseAiCommentLoading] = useState(false);


    const pollExerciseAiComment = async (aiAsstMsgId, attempt = 0) => {

        if (exerciseAiPollingIdRef.current !== aiAsstMsgId) {
            return;
        }

        const MAX_ATTEMPTS = 15;
        const POLLING_INTERVAL = 2000;

        if (attempt >= MAX_ATTEMPTS) {

            console.warn('Exercise AI Comment polling timeout');

            exerciseAiRequestRef.current = false;
            exerciseAiPollingIdRef.current = null;
            exerciseAiPollingTimerRef.current = null;
            setExerciseAiCommentLoading(false);

            return;
        }

        const jToken = localStorage.getItem('jToken');

        try {

            const res = await axiosApi.get(
                '/exercise/aiComment/result',
                {
                    headers: {
                        Authorization: `Bearer ${jToken}`
                    },
                    params: {
                        aiAsstMsgId
                    },
                    noLoading: true,
                }
            );

            if (exerciseAiPollingIdRef.current !== aiAsstMsgId) {
                return;
            }

            const result = res.data?.result;

            console.log('Exercise AI Comment polling:', result);

            if (!result) {

                exerciseAiRequestRef.current = false;
                exerciseAiPollingIdRef.current = null;
                exerciseAiPollingTimerRef.current = null;
                setExerciseAiCommentLoading(false);

                return;
            }

            if (result.aiStatCd === 'S') {

                setExerciseAiComment(result.aiAsstMsg ?? '');
                setExerciseAiCommentLoading(false);

                exerciseAiRequestRef.current = false;
                exerciseAiPollingIdRef.current = null;
                exerciseAiPollingTimerRef.current = null;

                return;
            }

            if (result.aiStatCd === 'F') {

                console.error('Exercise AI Comment 처리 실패');

                exerciseAiRequestRef.current = false;
                exerciseAiPollingIdRef.current = null;
                exerciseAiPollingTimerRef.current = null;
                setExerciseAiCommentLoading(false);

                return;
            }

            exerciseAiPollingTimerRef.current = setTimeout(() => {
                pollExerciseAiComment(aiAsstMsgId, attempt + 1);
            }, POLLING_INTERVAL);

        } catch (error) {

            console.error('Exercise AI Comment polling 오류:', error);

            exerciseAiRequestRef.current = false;
            exerciseAiPollingIdRef.current = null;
            exerciseAiPollingTimerRef.current = null;
            setExerciseAiCommentLoading(false);
        }
    };


    const startExerciseAiPolling = (aiAsstMsgId) => {

        if (!aiAsstMsgId) {
            return;
        }

        if (exerciseAiPollingIdRef.current === aiAsstMsgId) {
            return;
        }

        if (exerciseAiPollingTimerRef.current) {
            clearTimeout(exerciseAiPollingTimerRef.current);
        }

        exerciseAiPollingIdRef.current = aiAsstMsgId;

        pollExerciseAiComment(aiAsstMsgId);
    };


    const getExerciseAiComment = async (toDayExercList, targetRecDe) => {

        if (exerciseAiRequestRef.current) {
            return;
        }

        exerciseAiRequestRef.current = true;
        setExerciseAiCommentLoading(true);

        const loginUser = JSON.parse(
            sessionStorage.getItem('loginUser')
        );

        const patientId = loginUser?.usrHsptlMap
            ?.find((a) => a.cnncHsptlCd === '012')
            ?.patientId;

        const jToken = localStorage.getItem('jToken');

        /*
        * AI 스키마에 없는 lastMdfyDt 제거
        */
        const aiExerciseList = (toDayExercList ?? []).map((item) => {
            const { lastMdfyDt, ...exercise } = item;
            return exercise;
        });

        const requestData = {
            recDe: targetRecDe,
            patientId,
            drOrglData: JSON.stringify(aiExerciseList)
        };

        try {

            const res = await axiosApi.post(
                '/exercise/aiComment',
                requestData,
                {
                    headers: {
                        Authorization: `Bearer ${jToken}`
                    },
                    noLoading: true,
                }
            );

            const aiAsstMsgId = res.data?.result;

            if (aiAsstMsgId) {

                startExerciseAiPolling(aiAsstMsgId);

            } else {

                exerciseAiRequestRef.current = false;
                setExerciseAiCommentLoading(false);
            }

        } catch (error) {

            console.error('Exercise AI Comment 오류:', error);

            exerciseAiRequestRef.current = false;
            setExerciseAiCommentLoading(false);
        }
    };

    useEffect(() => {

        if (exerciseAiPollingTimerRef.current) {
            clearTimeout(exerciseAiPollingTimerRef.current);
        }

        exerciseAiPollingTimerRef.current = null;
        exerciseAiPollingIdRef.current = null;
        exerciseAiRequestRef.current = false;

        setExerciseAiComment('');
        setExerciseAiCommentLoading(false);

    }, [recDe]);

    useEffect(() => {

        return () => {

            if (exerciseAiPollingTimerRef.current) {
                clearTimeout(exerciseAiPollingTimerRef.current);
            }

            exerciseAiPollingTimerRef.current = null;
            exerciseAiPollingIdRef.current = null;
            exerciseAiRequestRef.current = false;
        };

    }, []);

    //ai 코멘트용


    /** 선택한 날짜의 기록을 다시 불러온다. 추가·삭제 후에도 이걸 부른다. */
    // const loadDaily = useCallback(async () => {
    //     try {
    //         setDaily(await getExerciseList(recDe));
    //     } catch (e) {
    //         console.error('운동 기록 조회 실패', e);
    //         setDaily(null);
    //     }
    // }, [recDe]);

    const loadDaily = useCallback(async () => {

        try {

            const result = await getExerciseList(recDe);

            setDaily(result);

            const toDayExercList = result?.list ?? [];
            const toDayExercAiComment =
                result?.toDayExercAiComment ?? {};

            // 선택 날짜 변경 시 이전 문구 제거
            setExerciseAiComment('');

            if (toDayExercAiComment?.aiStatCd === 'S') {

                // 완료된 AI 코멘트
                setExerciseAiComment(
                    toDayExercAiComment.aiAsstMsg ?? ''
                );

                setExerciseAiCommentLoading(false);

            } else if (toDayExercAiComment?.aiStatCd === 'W') {

                // 기존 요청 처리 중
                setExerciseAiCommentLoading(true);
                exerciseAiRequestRef.current = true;

                startExerciseAiPolling(
                    toDayExercAiComment.aiAsstMsgId
                );

            } else {

                /*
                * 현재 백엔드는 오늘 EXCS 기준으로 AI 요청하기 때문에
                * 신규 요청은 오늘 날짜만 한다.
                */
                const today = dayjs().format('YYYYMMDD');

                if (recDe === today) {

                    // 운동 기록 없어도 [] 로 전송
                    getExerciseAiComment(
                        toDayExercList,
                        recDe
                    );
                }
            }

        } catch (e) {

            console.error('운동 기록 조회 실패', e);
            setDaily(null);
        }

    }, [recDe]);

    /**
     * 기록이 있는 날에 점을 찍는다.
     * 끝은 항상 오늘이다. 선택한 날짜에서 끊으면 과거 날짜를 고른 뒤 최근 주로 돌아왔을 때 점이 비어 보인다.
     */
    const loadMarks = useCallback(async () => {
        try {
            const today = dayjs().startOf('day');
            const from = (selectedDate.isBefore(today, 'day') ? selectedDate : today).subtract(MARK_DAYS, 'day');
            const startDe = toRecDe(from.format('YYYY-MM-DD'));
            const endDe = toRecDe(today.format('YYYY-MM-DD'));
            const list = await getExerciseSummary(startDe, endDe);

            // WeeklyDateSwiper 는 YYYYMMDD 를 키로 쓴다. recDe 가 이미 그 형식이다.
            const marks = {};
            list.forEach((sum) => {
                // marks[sum.recDe] = 'good';
                marks[sum.recDe] = Number(sum.totMin ?? 0) >= GOAL.minutes ? 'good' : 'warn';
            });
            setDayMarks(marks);
        } catch (e) {
            console.error('운동 집계 조회 실패', e);
        }
    }, [selectedDate]);

    useEffect(() => {
        loadDaily();
        loadMarks();
    }, [loadDaily, loadMarks]);

    /** 워치 운동이 새로 들어왔을 때 */
    const handleWatchSynced = useCallback(() => {
        loadDaily();
        loadMarks();
    }, [loadDaily, loadMarks]);

    const { access, askPermission } = useWatchExerciseSync(handleWatchSynced);

    // 시스템 동의 화면 앞에 우리 안내 모달을 먼저 띄운다
    useEffect(() => {
        if (access === EXERCISE_ACCESS.NEED_PERMISSION || access === EXERCISE_ACCESS.NOT_INSTALLED) {
            setPermOpen(true);
        }
    }, [access]);

    const needsInstall = access === EXERCISE_ACCESS.NOT_INSTALLED;

    const handlePermConfirm = () => {
        setPermOpen(false);
        if (needsInstall) openSamsungHealthStore();
        else askPermission();
    };

    const exercises = (daily?.list ?? []).map(toViewItem);
    const totMin = daily?.totMin ?? 0;
    const totCalor = daily?.totCalor ?? 0;

    const stat = {
        duration: {
            current: totMin, goal: GOAL.minutes,
            percent: percent(totMin, GOAL.minutes), unit: '분',
        },
        calorie: {
            current: totCalor, goal: GOAL.calorie,
            percent: percent(totCalor, GOAL.calorie), unit: 'kcal',
        },
    };

    const handleAiChat = useCallback(() => {
        navigate(URL.CHATBOT , { state: { mode: 'exercise', aiComment : exerciseAiComment } });
    },[exerciseAiComment]);

    const handleAdd = () => {
        navigate(URL.EXERCISE_RECORD_ADD, { state: { recDe } });
    };

    const handleEdit = (item) => {
        navigate(URL.EXERCISE_RECORD_ADD, { state: { mode: 'edit', item, recDe } });
    };

    const handleRequestDelete = (item) => setDeleteTarget(item);

    const handleConfirmDelete = async () => {
        const target = deleteTarget;
        setDeleteTarget(null);
        if (!target) return;

        try {
            const ok = await deleteExercise(target.id);
            if (!ok) {
                alert('삭제하지 못했습니다.');
                return;
            }
            await Promise.all([loadDaily(), loadMarks()]);
        } catch (e) {
            console.error('운동 기록 삭제 실패', e);
            alert('삭제하지 못했습니다.');
        }
    };

    // 칼로리 부족 여부
    const isCalorieShort = stat.calorie.percent < 100;

    return (
        <>
            <HeadSub title="운동 기록" />

            <WeeklyDateSwiper
                selectedDate={selectedDate}
                onSelectDate={setSelectedDate}
                dayMarks={dayMarks}
            />

            {dayjs().format('YYYYMMDD') === recDe &&
                <RecommendBox
                    title="AI 운동 추천"
                    description={
                        exerciseAiCommentLoading
                            ? 'AI가 오늘의 운동을 분석하고 있습니다.'
                            : exerciseAiComment
                    }
                    onClick={handleAiChat}
                />
            }

            <div id="sub-content" data-top-margin="0">
                <div className="inner-wrap" data-top-padding="0">

                    {/* ── 오늘의 운동 기록 ── */}
                    <section className={style.section}>
                        <h3 className={style.sectionTitle}>오늘의 운동 기록</h3>
                        <div className={style.recordList}>
                            {exercises.length === 0 ? (
                                <div className={style.emptyBox}>
                                    <p>등록된 운동이 없습니다.</p>
                                </div>
                            ) : (
                                exercises.map((item) => (
                                    <ExerciseRecordCard
                                        key={item.id}
                                        item={item}
                                        sourceLabel={SOURCE_LABELS[item.source] || ''}
                                        onEdit={() => handleEdit(item)}
                                        onDelete={() => handleRequestDelete(item)}
                                    />
                                ))
                            )}
                        </div>
                        <button type="button" className={style.addBtn} onClick={handleAdd}>
                            운동 추가하기
                        </button>
                    </section>

                    {/* ── 오늘의 운동 현황 ── */}
                    <section className={style.section}>
                        <div className={style.sectionHead}>
                            <h3 className={style.sectionTitle}>오늘의 운동 현황</h3>
                            <div className={style.legend} aria-hidden="true">
                                <span className={`${style.legendDot} ${style.legendWarn}`} />부족
                                <span className={`${style.legendDot} ${style.legendGood}`} />양호
                            </div>
                        </div>

                        <div className={style.sectionBox}>
                            {/* <ExerciseStat data={stat} /> */}
                            <ExerciseDurationStat data={stat} />

                            {/* 소모 칼로리 미노출  2026.09.17 */}
                            {/* {isCalorieShort && (
                                <div className={style.alertBar}>
                                    <img src={iconExclMark} alt="주의 아이콘" />
                                    <span>칼로리 소모가 부족합니다.</span>
                                </div>
                            )} */}
                        </div>

                    </section>

                </div>
            </div>

            <ConfirmModal
                open={deleteTarget !== null}
                title="운동 삭제"
                message={deleteTarget ? <>'<b>{deleteTarget.name}</b>'을 삭제하시겠습니까?</> : ''}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={handleConfirmDelete}
                onCancel={() => setDeleteTarget(null)}
                confirmType="primary"
            />

            {/* 삼성 헬스 권한 허용 팝업 */}
            <Modal isOpen={permOpen} setIsOpen={setPermOpen}>
                <div className='modal-box' data-type="a">
                    <div className='modal-top-box'>
                        <div className='title-box'><p>{needsInstall ? '삼성 헬스 설치' : '권한 허용'}</p></div>
                        <div className='sub-box'>
                            {needsInstall ? (
                                <span>갤럭시 워치에서 측정한 운동 데이터를 <br />
                                      가져오려면 삼성 헬스 앱이 필요합니다. <br />
                                        <br />
                                      스토어에서 삼성 헬스를 설치해 주세요.
                                </span>
                            ) : (
                                <span>갤럭시 워치에서 측정한 걸음수·운동·혈압 데이터를 <br />
                                      자동으로 가져오기 위해 권한 동의가 필요합니다. <br />
                                        <br />
                                      권한 허용은 설정 &gt; 데이터권한에서 할 수 있습니다.
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="modal-bottom-box">
                        <div className='btn-layout'>
                            <button className="btn" data-btn-type="fill-gray" data-btn-size="md" onClick={() => setPermOpen(false)}><span>취소</span></button>
                            <button className="btn" data-btn-type="fill-main" data-btn-size="md" onClick={handlePermConfirm}>
                                <span>{needsInstall ? '설치하러 가기' : '권한 허용하기'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default ExerciseRecordMain;
